import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Tooltip,
  Autocomplete,
  Divider,
  CardMedia,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  FolderShared as FolderSharedIcon,
  Label as LabelIcon,
  Add as AddIcon,
  Share as ShareIcon,
  Image as ImageIcon,
  Group as GroupIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  RemoveCircleOutline as RemoveIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { Collection, CreateCollectionDTO } from '../models/Collection';
import { Group } from '../models/Group';
import { BASE_URL } from "../constants/global";
import { getImageUrl } from "../utils/imageUrls";
import { collectionApi, groupApi, exportApi } from '../utils/api';
import AuthenticatedImage from '../components/AuthenticatedImage';
import {
  useCollection, useCollectionImages, useMyGroups,
  useUpdateCollection, useDeleteCollection, useShareCollection, useUnshareCollection,
} from '../hooks/useQueries';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';

const COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#795548', '#607D8B', '#F44336', '#3F51B5',
];

const CollectionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hoveredImage, setHoveredImage] = useState<number | null>(null);
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCollection, setEditCollection] = useState<CreateCollectionDTO>({
    name: '',
    description: '',
    color: COLORS[0],
    labels: [],
  });
  const [newLabel, setNewLabel] = useState('');
  
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  const collectionId = parseInt(id || '0');

  const { data: collection, isLoading: collectionLoading } = useCollection(collectionId);
  const { data: images = [], isLoading: imagesLoading } = useCollectionImages(collectionId);
  const { data: myGroups = [] } = useMyGroups();

  const loading = collectionLoading || imagesLoading;

  const updateMutation = useUpdateCollection();
  const deleteMutation = useDeleteCollection();
  const shareMutation = useShareCollection();
  const unshareMutation = useUnshareCollection();
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (collection) {
      setEditCollection({
        name: collection.name,
        description: collection.description || '',
        color: collection.color || COLORS[0],
        labels: collection.labels || [],
      });
    }
  }, [collection]);

  const filteredImages = useMemo(() => {
    if (!searchQuery.trim()) return images;
    const query = searchQuery.toLowerCase();
    return images.filter(img => 
      img.id?.toString().includes(query) ||
      img.siteId?.toLowerCase().includes(query) ||
      img.descriptorId?.toLowerCase().includes(query) ||
      img.authorIdentity?.toLowerCase().includes(query) ||
      img.species?.toLowerCase().includes(query) ||
      img.address?.toLowerCase().includes(query)
    );
  }, [searchQuery, images]);

  const handleUpdateCollection = async () => {
    try {
      setSaving(true);
      await updateMutation.mutateAsync({ id: collectionId, data: editCollection });
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update collection');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCollection = async () => {
    if (!window.confirm('Are you sure you want to delete this collection?')) return;
    
    try {
      await deleteMutation.mutateAsync(collectionId);
      navigate('/collections');
    } catch (err: any) {
      setError(err.message || 'Failed to delete collection');
    }
  };

  const handleShare = async () => {
    if (!selectedGroup) return;
    
    try {
      setSaving(true);
      await shareMutation.mutateAsync({ collectionId, groupId: selectedGroup.id });
      setShareDialogOpen(false);
      setSelectedGroup(null);
    } catch (err: any) {
      setError(err.message || 'Failed to share collection');
    } finally {
      setSaving(false);
    }
  };

  const handleUnshare = async () => {
    try {
      await unshareMutation.mutateAsync(collectionId);
    } catch (err: any) {
      setError(err.message || 'Failed to unshare collection');
    }
  };

  const handleAddLabel = async () => {
    if (!newLabel.trim()) return;
    
    try {
      const token = await getAccessTokenSilently();
      await collectionApi.addLabel(collectionId, newLabel.trim(), token);
      queryClient.invalidateQueries({ queryKey: ['collections', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['collections', 'labels'] });
      setNewLabel('');
    } catch (err: any) {
      setError(err.message || 'Failed to add label');
    }
  };

  const handleRemoveLabel = async (label: string) => {
    try {
      const token = await getAccessTokenSilently();
      await collectionApi.removeLabel(collectionId, label, token);
      queryClient.invalidateQueries({ queryKey: ['collections', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['collections', 'labels'] });
    } catch (err: any) {
      setError(err.message || 'Failed to remove label');
    }
  };

  const handleRemoveImage = async (imageId: number) => {
    try {
      const token = await getAccessTokenSilently();
      await collectionApi.removeImage(collectionId, imageId, token);
      queryClient.invalidateQueries({ queryKey: ['collections', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['collections', collectionId, 'images'] });
      queryClient.invalidateQueries({ queryKey: ['collections', collectionId, 'imageIds'] });
    } catch (err: any) {
      setError(err.message || 'Failed to remove image');
    }
  };

  const handleExportToExcel = async () => {
    try {
      const token = await getAccessTokenSilently();
      const blob = await exportApi.exportCollectionToExcel(collectionId, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collection_${collection?.name || collectionId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export collection');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!collection) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Collection not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/collections')} 
        sx={{ mb: 2, minHeight: 44 }}
      >
        Back to Collections
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, borderLeft: `4px solid ${collection.color || '#2196F3'}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Avatar sx={{ width: { xs: 64, sm: 80 }, height: { xs: 64, sm: 80 }, bgcolor: collection.color || 'primary.main' }}>
            {collection.isShared ? <FolderSharedIcon sx={{ fontSize: 40 }} /> : <FolderIcon sx={{ fontSize: 40 }} />}
          </Avatar>
          
          <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Typography variant="h4" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{collection.name}</Typography>
            
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {collection.description || 'No description'}
            </Typography>

            {collection.researchPaperUrl && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'inline' }}>
                  Research Paper:{' '}
                </Typography>
                <Typography
                  component="a"
                  href={collection.researchPaperUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {collection.researchPaperUrl}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ImageIcon color="action" />
                <Typography variant="body2">{collection.imageCount} images</Typography>
              </Box>
              {collection.groupName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <GroupIcon color="action" />
                  <Typography variant="body2">Shared with {collection.groupName}</Typography>
                </Box>
              )}
            </Box>

            {/* Labels */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {collection.labels?.map(label => (
                <Chip
                  key={label}
                  icon={<LabelIcon />}
                  label={label}
                  onDelete={() => handleRemoveLabel(label)}
                  size="small"
                />
              ))}
              <Box sx={{ display: 'flex', gap: 0.5, width: { xs: '100%', sm: 'auto' } }}>
                <TextField
                  size="small"
                  placeholder="Add label"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddLabel()}
                  sx={{ width: { xs: '100%', sm: 120 } }}
                  inputProps={{ style: { fontSize: 16 } }}
                />
                <IconButton size="small" onClick={handleAddLabel} disabled={!newLabel.trim()} sx={{ minWidth: 40, minHeight: 40 }}>
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            <Tooltip title="Export to Excel">
              <IconButton onClick={handleExportToExcel} color="success" sx={{ minWidth: 44, minHeight: 44 }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Collection">
              <IconButton onClick={() => setEditDialogOpen(true)} sx={{ minWidth: 44, minHeight: 44 }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            {!collection.isShared ? (
              <Tooltip title="Share with Group">
                <IconButton color="primary" onClick={() => setShareDialogOpen(true)} sx={{ minWidth: 44, minHeight: 44 }}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button size="small" onClick={handleUnshare} sx={{ minHeight: 44 }}>Unshare</Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Images Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>Images ({filteredImages.length})</Typography>
          <TextField
            size="small"
            placeholder="Search images..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
            inputProps={{ style: { fontSize: 16 } }}
          />
        </Box>

        {images.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No images in this collection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add images from the image viewer page
            </Typography>
          </Box>
        ) : filteredImages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No images match your search
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 2 } }}>
            {filteredImages.map(image => (
              <Box key={image.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.33% - 11px)', lg: 'calc(25% - 12px)' } }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <Box
                    sx={{ position: 'relative' }}
                    onMouseEnter={() => setHoveredImage(image.id)}
                    onMouseLeave={() => setHoveredImage(null)}
                  >
                    <AuthenticatedImage
                      src={image.fileName ? getImageUrl(image.fileName, "display") : ""}
                      alt={`Image ${image.id}`}
                      height={200}
                      onClick={() => navigate(`/image/${image.id}`)}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: 'grey.200',
                      }}
                    />
                    {hoveredImage === image.id && (
                      <Box
                        sx={{
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 9999,
                          bgcolor: 'rgba(0, 0, 0, 0.9)',
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 24,
                          maxWidth: '80vw',
                          maxHeight: '80vh',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AuthenticatedImage
                          src={image.fileName ? getImageUrl(image.fileName, "display") : ""}
                          alt={`Image ${image.id}`}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: '70vh',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main' },
                        }}
                        onClick={() => navigate(`/image/${image.id}`)}
                      >
                        {image.uniqueImageId || `ID: ${image.id}`}
                      </Typography>
                      <Tooltip title="Remove from collection">
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveImage(image.id)}
                          sx={{ mt: -0.5, mr: -0.5 }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {image.authorIdentity && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {image.authorIdentity}
                        </Typography>
                      </Box>
                    )}

                    {image.pictureDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(image.pictureDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}

                    {image.species && (
                      <Chip 
                        label={image.species} 
                        size="small" 
                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                      />
                    )}

                    {image.siteId && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Site: {image.siteId}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          }
        }}
      >
        <DialogTitle>Edit Collection</DialogTitle>
        <DialogContent sx={{ pb: { xs: 2, sm: 3 } }}>
          <TextField
            autoFocus
            margin="dense"
            label="Collection Name"
            fullWidth
            value={editCollection.name}
            onChange={(e) => setEditCollection({ ...editCollection, name: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={editCollection.description}
            onChange={(e) => setEditCollection({ ...editCollection, description: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />
          <TextField
            margin="dense"
            label="Research Paper URL"
            fullWidth
            placeholder="https://..."
            value={editCollection.researchPaperUrl || ''}
            onChange={(e) => setEditCollection({ ...editCollection, researchPaperUrl: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: 16 } }}
          />

          <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {COLORS.map(color => (
              <Box
                key={color}
                sx={{
                  width: { xs: 40, sm: 32 },
                  height: { xs: 40, sm: 32 },
                  borderRadius: '50%',
                  bgcolor: color,
                  cursor: 'pointer',
                  border: editCollection.color === color ? '3px solid black' : 'none',
                }}
                onClick={() => setEditCollection({ ...editCollection, color })}
              />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Button 
            color="error" 
            startIcon={<DeleteIcon />} 
            onClick={handleDeleteCollection}
            sx={{ minHeight: 44 }}
          >
            Delete Collection
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateCollection} 
            variant="contained" 
            disabled={!editCollection.name.trim() || saving}
            sx={{ minHeight: 44 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          }
        }}
      >
        <DialogTitle>Share Collection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a group to share this collection with. Group members will be able to view all images in this collection.
          </Typography>
          <Autocomplete
            options={myGroups}
            getOptionLabel={(option) => option.name}
            value={selectedGroup}
            onChange={(_, value) => setSelectedGroup(value)}
            renderInput={(params) => <TextField {...params} label="Select Group" />}
          />
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          <Button 
            onClick={() => setShareDialogOpen(false)}
            sx={{ minHeight: 44 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleShare} 
            variant="contained" 
            disabled={!selectedGroup || saving}
            sx={{ minHeight: 44 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Fix missing import
const Delete = DeleteIcon;

export default CollectionDetail;
